import { useField } from 'formik'
import { motion } from 'framer-motion'
import { isNil } from 'ramda'
import { useMemo, useRef } from 'react'
import { useTheme } from 'styled-components'

import { Spacing } from '../../layout/index'
import { P } from '../../typography/Body'
import { Label } from '../label/Label'
import { StBottomLabelsContainer, StDataList, StFieldContainer, StInputContainer, StRangeInput } from './Range.styled'

import type { InputHTMLAttributes } from 'react'
import type { PartiallyRequired } from '@/utils/typescript'

type InputProps = {
  label?: string
  noMargin?: boolean
  maxLabel?: string
  minLabel?: string
  numberOfTicks?: number
  showTickmarks?: boolean
} & PartiallyRequired<InputHTMLAttributes<HTMLInputElement>, 'name'>

const AnimatedFieldContainer = motion(StFieldContainer)

const Range = ({ label, noMargin, maxLabel, minLabel, numberOfTicks, showTickmarks, ...props }: InputProps) => {
  const theme = useTheme()
  const inputRef = useRef<HTMLInputElement>(null)
  const [field] = useField(props)

  // Template variables
  // ------------------------------------------------------------------------- /
  const defaultValue = useMemo(() => {
    if (!isNil(props.max) && !isNil(props.min)) {
      const min = props.min as number
      const max = props.max as number
      const step = (props.step as number) ?? 1 // Default step to 1 if not provided
      const midValue = (max - min) / 2 + min

      // Round to the nearest step
      return Math.round(midValue / step) * step
    }

    return 0
  }, [props.max, props.min, props.step])

  const value = isNil(field.value) ? defaultValue : field.value

  const min = props.min as number
  const max = props.max as number
  const trackPercentageValue = ((value - min) / (max - min)) * 100

  const valuesListId = `${props.name}-values`

  // Render Methods
  // ------------------------------------------------------------------------- /
  function renderTicks() {
    if (!isNil(props.max) && !isNil(props.min)) {
      const amount = numberOfTicks || 11
      const min = props.min as number
      const max = props.max as number
      const stepSize = (max - min) / (amount - 1)

      const tickArray = Array.from({ length: amount }).map((_, index) => index)

      return (
        <StDataList id={valuesListId}>
          {tickArray.map((tickIndex) => {
            const label = min + tickIndex * stepSize
            return <option key={label} value={tickIndex} label={label.toString()} />
          })}
        </StDataList>
      )
    }
  }

  return (
    <AnimatedFieldContainer noMargin={noMargin}>
      {label ? (
        <>
          <Label htmlFor={props.name}>
            {label} {props.required ? '*' : null}
          </Label>
          <Spacing height={theme.UI.Spacing.Space3} />
        </>
      ) : null}
      {showTickmarks ? renderTicks() : null}
      <StInputContainer>
        <StRangeInput
          {...field}
          id={props.id}
          step={props.step}
          min={props.min}
          max={props.max}
          value={value}
          trackPercentageValue={trackPercentageValue}
          type='range'
          list={showTickmarks ? valuesListId : undefined}
          ref={inputRef}
        />
      </StInputContainer>
      {minLabel && maxLabel ? (
        <StBottomLabelsContainer>
          <P type='BodyMediumRegular' style={{ margin: 0, color: theme.Colors.Neutrals.Dark }}>
            {minLabel}
          </P>
          <P type='BodyMediumRegular' style={{ margin: 0, color: theme.Colors.Neutrals.Dark }}>
            {maxLabel}
          </P>
        </StBottomLabelsContainer>
      ) : null}
    </AnimatedFieldContainer>
  )
}

export { Range }
